import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import {
  useAdsActivities,
  useDeleteAdsActivity,
} from '../../shared/hooks/UseAdsActivities';
import AdsActivitiesTableView from './_components/AdsActivitiesTableView';
import AdsActivityFilters from './_components/AdsActivityFilters';
import AdsActivityStats from './_components/AdsActivityStats';
import Header from '../../components/template/Header';
import BreadCrumb from '../../components/template/BreadCrumb';
import Spinner from '../../components/layout/Spinner';
import toast from 'react-hot-toast';

const AdsActivitiesList = () => {
  const location = useLocation();
  const history = useHistory();

  // Parse initial filters from URL query params
  const getInitialFilters = useCallback(() => {
    const searchParams = new URLSearchParams(location.search);
    return {
      page: parseInt(searchParams.get('page')) || 1,
      limit: parseInt(searchParams.get('limit')) || 10,
      status: searchParams.get('status') || '',
      platform: searchParams.get('platform') || '',
      assigned_to: searchParams.get('assigned_to') || '',
      client: searchParams.get('client') || '',
    };
  }, [location.search]);

  const [filters, setFilters] = useState(getInitialFilters);
  const [sidebarVisible, setSidebarVisible] = useState(true);

  // Memoize filters to prevent unnecessary re-renders
  const memoizedFilters = useMemo(
    () => filters,
    [
      filters.page,
      filters.limit,
      filters.status,
      filters.platform,
      filters.assigned_to,
      filters.client,
    ]
  );

  const [{ activities, pagination, loading, error }, refetch] =
    useAdsActivities(memoizedFilters);
  const [deleteActivity, { loading: deleteLoading }] = useDeleteAdsActivity();

  // Update URL when filters change
  const updateURL = useCallback(
    (newFilters) => {
      const searchParams = new URLSearchParams();

      Object.entries(newFilters).forEach(([key, value]) => {
        if (value && value !== '' && value !== 1 && value !== 10) {
          searchParams.set(key, value.toString());
        }
      });

      const newURL = `${location.pathname}?${searchParams.toString()}`;
      history.replace(newURL);
    },
    [location.pathname, history]
  );

  const handleFilterChange = useCallback(
    (newFilters) => {
      const updatedFilters = {
        ...filters,
        ...newFilters,
        page: 1, // Reset to first page when filters change
      };

      setFilters(updatedFilters);
      // Update URL immediately for filter changes
      updateURL(updatedFilters);
    },
    [filters, updateURL]
  );

  const handlePageChange = useCallback(
    (page) => {
      const updatedFilters = { ...filters, page };
      setFilters(updatedFilters);
      // Update URL immediately for page changes
      updateURL(updatedFilters);
    },
    [filters, updateURL]
  );

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this ads activity?')) {
      try {
        await deleteActivity(id);
        toast.success('Ads activity deleted successfully');
        refetch();
      } catch (error) {
        toast.error('Failed to delete ads activity');
      }
    }
  };

  const toggleSidebar = useCallback(() => {
    setSidebarVisible(!sidebarVisible);
  }, [sidebarVisible]);

  // Sync filters with URL when location changes (e.g., browser back/forward)
  useEffect(() => {
    const urlFilters = getInitialFilters();
    setFilters(urlFilters);
  }, [location.search, getInitialFilters]);

  // Memoize the clear filters function to prevent recreation
  const handleClearAllFilters = useCallback(() => {
    const clearedFilters = {
      page: 1,
      limit: 10,
      status: '',
      platform: '',
      assigned_to: '',
      client: '',
    };
    setFilters(clearedFilters);
    updateURL(clearedFilters);
  }, [updateURL]);

  if (loading) return <Spinner />;
  if (error)
    return (
      <div className='alert alert-danger'>Error loading ads activities</div>
    );

  return (
    <div className='pace-done'>
      <div>
        <Header />
        <BreadCrumb
          title='Ads Activities'
          mainLinkTitle='Dashboard'
          mainLinkUrl='/dashboard'
          activeLink='Ads Activities'
        />

        <div className='container-fluid' style={{ padding: '20px' }}>
          <div className='row'>
            <div className='mb-4'>
              <AdsActivityStats />
            </div>
            {/* Sidebar Filters */}
            <div
              className={`${
                sidebarVisible ? 'col-lg-2 col-md-3' : 'd-none'
              } mb-4`}
            >
              <div className='ads-activities-sidebar'>
                {/* Header */}
                <div className='sidebar-header mb-3'>
                  <div className='d-flex justify-content-between align-items-center mb-2'>
                    <h6 className='page-title mb-0'>Filters</h6>
                    <button
                      className='btn btn-sm btn-outline-secondary'
                      onClick={toggleSidebar}
                      title='Hide Filters'
                    >
                      <i className='fas fa-times'></i>
                    </button>
                  </div>
                  <Link
                    to='/ads-activities/create'
                    className='btn btn-primary btn-sm w-100'
                  >
                    <i className='fas fa-plus me-1'></i>
                    New Activity
                  </Link>
                </div>

                {/* Filters */}
                <div
                  className='filters-sidebar'
                  // style={{ border: '1px solid blue' }}
                >
                  <AdsActivityFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearAll={handleClearAllFilters}
                  />
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div
              className={`${sidebarVisible ? 'col-lg-10 col-md-9' : 'col-12'}`}
            >
              <div className='main-content-area'>
                {/* Toggle Sidebar Button */}
                {!sidebarVisible && (
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <div className='d-flex align-items-center'>
                      <button
                        className='btn btn-outline-secondary btn-sm me-3'
                        onClick={toggleSidebar}
                        title='Show Filters'
                      >
                        <i className='fas fa-filter me-2'></i>
                        Show Filters
                      </button>
                      <span className='text-muted small'>
                        <i className='fas fa-info-circle me-1'></i>
                        Filters are hidden. Click "Show Filters" to display
                        them.
                      </span>
                    </div>
                  </div>
                )}

                {/* Activities Table */}
                <AdsActivitiesTableView
                  activities={activities}
                  onDelete={handleDelete}
                  deleteLoading={deleteLoading}
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  loading={loading}
                />
              </div>
            </div>
          </div>
        </div>

        <style jsx>{`
          .ads-activities-sidebar {
            background: #f8f9fa;
            border-radius: 6px;
            padding: 15px;
            border: 1px solid #e9ecef;
            height: fit-content;
            position: sticky;
            top: 20px;
            z-index: 100;
          }

          .sidebar-header {
            border-bottom: 1px solid #e9ecef;
            padding-bottom: 10px;
          }

          .sidebar-header .page-title {
            font-size: 1rem;
            font-weight: 600;
            color: #2d3748;
            margin: 0;
          }

          .filters-sidebar .card {
            border: none;
            box-shadow: none;
            background: transparent;
          }

          .filters-sidebar .card-header {
            background: transparent;
            border: none;
            padding: 0 0 10px 0;
          }

          .filters-sidebar .card-body {
            padding: 0;
          }

          .main-content-area {
            background: white;
            border-radius: 8px;
            overflow: hidden;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          }

          @media (max-width: 991.98px) {
            .ads-activities-sidebar {
              position: static;
              margin-bottom: 20px;
            }
          }

          @media (max-width: 767.98px) {
            .sidebar-header .page-title {
              font-size: 1.25rem;
            }

            .ads-activities-sidebar {
              padding: 15px;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default AdsActivitiesList;
